import {
  Clubhouse_LeagueHandicapMethod,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  HandicapMode,
  HandicapPercentage,
  TeeType,
} from "../enums";
import type { Clubhouse_ClubhouseLeagueSettingsDto } from "../types/leagues";
import type { LeagueFormValues } from "./leagueFormSchema";

/**
 * The settings fields the create endpoint requires but Guided Setup never asks
 * about. Same values Advanced Setup fills in (see `DEFAULT_DRAFT_SETTINGS` in
 * advancedLeagueMapping) — handicapping is backend-owned and has no UI in
 * either flow, and a known-valid combination beats leaving the league with none.
 *
 * `participantCap` 0 is how both flows say "no limit" — the backend's own
 * reading of 0, not a placeholder. Guided no longer asks for a limit and the
 * settings DTO still requires the field, so 0 is sent rather than omitted. The
 * schema's own minimum is 1, so 0 is never a value an operator typed.
 */
const GUIDED_DEFAULTS = {
  participantCap: 0,
  handicapMethod: Clubhouse_LeagueHandicapMethod.NET,
  handicapMode: HandicapMode.ABSOLUTE,
  handicapPercentage: HandicapPercentage.SEVENTY,
  courseRatingSlope: true,
  // Guided does not ask for tees, but the league needs a pair: the roster
  // assigns a player their tee from these at sign-up. Blue/Red is product's
  // choice for Guided and is deliberately not Advanced's White/Red draft seed
  // — Advanced puts the rows on screen, so its seed is only a starting point
  // an operator then answers, where this pair is what Guided ships. An
  // operator can change both on the league's settings afterwards.
  mensTee: TeeType.BLUE,
  womensTee: TeeType.RED,
} as const;

/**
 * Guided Setup's answers as the league settings the create endpoint stores.
 *
 * Both flows now post the same `settings` object — there is no separate wizard
 * envelope — so this is Guided's counterpart to the advanced editor's
 * `buildSettings`, and the form's own `wizard` group is internal to the form.
 *
 * Deliberately omitted:
 *   - `allowAsynchronousMatchPlay`: unasked, and omitting it is what Guided has
 *     always done, so the match-play default is unchanged.
 */
export function toGuidedLeagueSettings(
  values: LeagueFormValues,
): Clubhouse_ClubhouseLeagueSettingsDto {
  const leagueType = values.wizard.leagueType;
  // Scramble skips the scoring step: a shared ball only scores as Stroke Play.
  const leagueScoringType =
    leagueType === Clubhouse_LeagueType.SCRAMBLE
      ? Clubhouse_LeagueScoring.STROKE
      : values.wizard.leagueScoringType;

  return {
    ...GUIDED_DEFAULTS,
    leagueType,
    leagueScoringType,
    // Guided does not ask for a team size; a team or scramble league gets the
    // same two players per team Advanced Setup applies. An individual league
    // must send none — the API rejects a team size it cannot use.
    ...(leagueType === Clubhouse_LeagueType.INDIVIDUAL
      ? {}
      : { leagueTeamSize: values.wizard.teamSize ?? 2 }),
  };
}
